import {
  FlowTrackerSteps,
  PARTIAL_TRANSFER_FAQ,
  validationErrorMessage,
} from 'common/constants';
import Accordion from 'components/common/Accordion/Accordion';
import {
  ATBeaconError,
  ATBeaconLoadingSpinner,
  ATBeaconNotification,
} from 'components/common/beacon/beaconRegistration';
import FlowTracker from 'components/common/FlowTracker/FlowTracker';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useGetHoldingsBySelectedAccountQuery } from 'src/api/atv/atvApi';
import { PageRoutes } from 'src/common/constants';
import { RootState } from 'src/state/store';
import {
  HoldingTransferType,
  partialTransferLabel,
  partialTransferMessage,
} from 'utils/constants';
import { Holding } from '../../../../api/atv/atvApi.types';
import { setSelectedHoldings } from '../../transferSlice';
import './PartialTransfer.scss';
import PartialTransferHolding from './partialTransferHolding/PartialTransferHolding';

interface PartialTransferProps {
  setIsStepValid: (isEnabled: boolean) => void;
  showValidation: boolean;
  setShowValidation: (isEnabled: boolean) => void;
}
const PartialTransfer: React.FC<PartialTransferProps> = (props) => {
  const { setIsStepValid, showValidation, setShowValidation } = props;
  const [notificationMessage, setNotificationMessage] = useState(
    validationErrorMessage.EMPTY_HOLDING,
  );
  const {
    data: holdings,
    isLoading,
    isSuccess,
    isError,
  } = useGetHoldingsBySelectedAccountQuery() as {
    data: any;
    isLoading: boolean;
    isSuccess: boolean;
    isError: boolean;
  };
  const selectedHoldings = useSelector(
    (state: RootState) => state.transfer.selectedHoldings,
  );

  const dispatch = useDispatch();

  useEffect(() => {
    if (isSuccess) {
      dispatch(
        setSelectedHoldings(
          holdings.map((holding: Holding) => ({
            ...holding,
            transferType: HoldingTransferType.TOTAL,
          })),
        ),
      );
    }
  }, [isSuccess, dispatch, holdings]);
  const navigate = useNavigate();
  useEffect(() => {
    if (isError) {
      navigate(PageRoutes.ERROR);
    }
  }, [isError, navigate]);
  useEffect(() => {
    setIsStepValid(true);
    setShowValidation(false);
  }, []);

  const getSelectedHolding = (holding: Holding) => {
    return selectedHoldings?.find((h: Holding) => h.id === holding.id) ?? null;
  };
  const calculateTotalquantity = (results: Holding[]) => {
    return results.reduce((total, holding) => total + holding.quantity, 0);
  };
  const updateSelectedHoldings = (
    holding: Holding,
    action: 'add' | 'remove' | 'update',
    updateVal?: any,
  ) => {
    let updatedHoldings = selectedHoldings ?? [];

    switch (action) {
      case 'add':
        updatedHoldings = [...updatedHoldings, holding];
        break;
      case 'remove':
        updatedHoldings = updatedHoldings.filter((h: Holding) => h.id !== holding.id);
        break;
      case 'update':
        updatedHoldings = updatedHoldings.map((h: Holding) =>
          h.id === holding.id ? { ...h, ...updateVal } : h,
        );
        break;
    }
    const totalquantity = calculateTotalquantity(updatedHoldings);
    if (updatedHoldings.length === 0) {
      setNotificationMessage(validationErrorMessage.EMPTY_HOLDING);
      setIsStepValid(false);
    } else if (Number(totalquantity) === 0) {
      setNotificationMessage(validationErrorMessage.ZERO_QUANTITY);
      setIsStepValid(false);
    } else {
      setIsStepValid(true);
    }
    dispatch(setSelectedHoldings(updatedHoldings));
  };
  const handleBeaconDismiss = () => {
    setShowValidation(false);
  };
  return (
    <div className="content-container b-grid">
      <div className="content-left b-grid-item b-xs:12-col b-sm:12-col b-md:8-col b-lg:8-col">
        <div className="b-grid">
          <span className="b-grid-item--full-col b-xs:12-col b-sm:12-col b-md:12-col b-lg:12-col">
            <label className="b-typography-sans-serif--headline--100 b-font-weight-bold">
              {partialTransferLabel}
            </label>
            <p>{partialTransferMessage}</p>
            {!isLoading && holdings && selectedHoldings && isSuccess && (
              <form id="partialTransferForm">
                {showValidation && (
                  <div className="b-grid-item--full-col b-xs:12-col b-sm:12-col b-md:12-col b-lg:12-col b-padding-block-end--micro--jumbo">
                    <ATBeaconNotification
                      type="inline"
                      variant="warning"
                      dismissible
                      onBeaconEventDismiss={() => handleBeaconDismiss()}
                    >
                      {notificationMessage}
                    </ATBeaconNotification>
                  </div>
                )}
                <label className="b-typography-serif--headline--100 b-font-weight-bold">
                  {selectedHoldings?.length} holdings available to transfer
                </label>
                <div className="holdings-wrapper">
                  {holdings.map((holding: any) => (
                    <PartialTransferHolding
                      key={holding.id}
                      holding={holding}
                      updateSelectedHoldings={updateSelectedHoldings}
                      selectedHolding={getSelectedHolding(holding)}
                      isManualEntry={false}
                      setNotificationMessage={(message: string) =>
                        setNotificationMessage(message)
                      }
                    />
                  ))}
                </div>
              </form>
            )}
            {!isLoading && isError && (
              <ATBeaconError>
                An error occurred while fetching the holdings. Please try again later.
              </ATBeaconError>
            )}
          </span>
        </div>
        {isLoading && (
          <div className="content-loading">
            <ATBeaconLoadingSpinner scale={'normal'} />
          </div>
        )}
      </div>
      <div className="b-grid-item--full-col b-xs:12-col b-sm:12-col b-md:4-col b-lg:4-col">
        <div className="flow-tracker-container">
          <FlowTracker currentFlowStep={2} items={FlowTrackerSteps} />
        </div>
        <div className="faq-container">
          <Accordion items={PARTIAL_TRANSFER_FAQ} />
        </div>
      </div>
    </div>
  );
};

export default PartialTransfer;
